import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';
import { 
  requestNotificationPermission, 
  onForegroundMessage,
  getFirebaseMessaging 
} from '../lib/firebase';

export const usePushNotifications = () => {
  const [fcmToken, setFcmToken] = useState(null);
  const [permissionStatus, setPermissionStatus] = useState('default');
  const [isSupported, setIsSupported] = useState(false);

  // Check if push notifications are supported
  useEffect(() => {
    const checkSupport = async () => {
      const supported = 'Notification' in window && 
                       'serviceWorker' in navigator && 
                       'PushManager' in window;
      setIsSupported(supported);
      
      if (supported) {
        setPermissionStatus(Notification.permission);
      }
    };
    
    checkSupport();
  }, []);

  // Register service worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/firebase-messaging-sw.js')
        .then((registration) => {
          console.log('Service Worker registered:', registration.scope);
        })
        .catch((error) => {
          console.error('Service Worker registration failed:', error);
        });
    }
  }, []);

  // Request permission and get token
  const enablePushNotifications = useCallback(async () => {
    try {
      const token = await requestNotificationPermission();
      
      if (token) {
        setFcmToken(token);
        setPermissionStatus('granted');
        
        // Send token to backend
        await axios.post(`${API}/notifications/fcm/register`, {
          fcm_token: token,
          device_type: 'web'
        });
        
        toast.success('Push notifications enabled!');
        return token;
      } else {
        setPermissionStatus(Notification.permission);
        if (Notification.permission === 'denied') {
          toast.error('Notification permission denied. Please enable in browser settings.');
        }
        return null;
      }
    } catch (error) {
      console.error('Error enabling push notifications:', error);
      toast.error('Failed to enable push notifications');
      return null;
    }
  }, []);

  // Listen for foreground messages
  useEffect(() => {
    let unsubscribe;
    
    const setupForegroundListener = async () => {
      unsubscribe = await onForegroundMessage((payload) => {
        // Show toast for foreground notifications
        const title = payload.notification?.title || 'Stelx Pay';
        const body = payload.notification?.body || 'New notification';
        
        toast(title, {
          description: body,
          action: {
            label: 'View',
            onClick: () => {
              // Navigate based on notification type
              const type = payload.data?.type;
              if (type === 'remittance' || type === 'transfer') {
                window.location.href = '/transactions';
              } else if (type === 'bill') {
                window.location.href = '/bills';
              }
            }
          }
        });
      });
    };
    
    if (permissionStatus === 'granted') {
      setupForegroundListener();
    }
    
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [permissionStatus]);

  // Disable push notifications
  const disablePushNotifications = useCallback(async () => {
    try {
      if (fcmToken) {
        await axios.post(`${API}/notifications/fcm/unregister`, {
          fcm_token: fcmToken
        });
        setFcmToken(null);
        toast.success('Push notifications disabled');
      }
    } catch (error) {
      console.error('Error disabling push notifications:', error);
    }
  }, [fcmToken]);

  return {
    fcmToken,
    permissionStatus,
    isSupported,
    enablePushNotifications,
    disablePushNotifications,
    isEnabled: permissionStatus === 'granted' && !!fcmToken
  };
};

export default usePushNotifications;
